/**
 * The six fixed Ishikawa (fishbone) categories, in display order, with the
 * colour and side of the spine each one owns.
 *
 * The category list is locked upstream: Arcola's aiid.v2 P4 RCA schema
 * (`rcaResponseSchema` in the platform's aiid-classify.ts) requires exactly
 * these six keys, each an array of at most 3 {Cause, Explanation} objects.
 * The AI_Incident_Tracker Python renderer that produced the original
 * pre-rendered diagrams (ishikawa.py) used the same six, three above the
 * spine and three below, and this file keeps that arrangement.
 *
 * Colours follow the Python renderer's assignment — red / orange / … /
 * green / blue / purple down the list — re-stepped onto the AIRI Navigator
 * register that DOMAIN_COLORS draws from, then validated as a categorical
 * palette (lightness band, chroma floor, CVD separation, normal-vision
 * separation, contrast) against both the light and dark chart surfaces.
 *
 * One substitution was forced. The Python "Human Factors" brown is
 * desaturated dark orange, so no re-stepping separates it from the
 * "Data Inputs" orange — it fails the chroma floor and the normal-vision
 * floor together. Teal takes that slot. Blue and purple land in the 6–8
 * CVD band, which is legal only alongside a second, non-colour cue: every
 * bone here is also directly labelled with its category name and fixed to
 * its own position on the spine.
 */
export interface IshikawaCategory {
  /** Key exactly as it appears in the stored JSON — do not rename. */
  key: string;
  colour: string;
  /** Bone sits above the spine (true) or below it (false). */
  top: boolean;
}

export const ISHIKAWA_CATEGORIES: readonly IshikawaCategory[] = [
  { key: 'Technology', colour: '#C24060', top: true },
  { key: 'Data Inputs', colour: '#D4762C', top: true },
  { key: 'Human Factors', colour: '#1E8FA8', top: true },
  { key: 'Process and Methods', colour: '#4E8C24', top: false },
  { key: 'Regulatory Environment', colour: '#2E5FC4', top: false },
  { key: 'Management', colour: '#A6399B', top: false },
] as const;
